import { supabase } from "@/integrations/supabase/client";

export interface Keyword {
  id: string;
  term: string;
  definition: string;
  synonyms: string[];
  glossaryVersion: string;
  status: "active" | "draft";
  certificationIds: string[] | null;
  createdAt: string;
  updatedAt: string;
}

type Listener = () => void;
let _items: Keyword[] = [];
let _loaded = false;
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((fn) => fn());

export function rowToKeyword(r: any): Keyword {
  return {
    id: r.id,
    term: r.term,
    definition: r.definition ?? "",
    synonyms: Array.isArray(r.synonyms) ? r.synonyms : [],
    glossaryVersion: r.glossary_version ?? "",
    status: r.status as "active" | "draft",
    certificationIds: Array.isArray(r.certification_ids) ? r.certification_ids : null,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

async function loadFromDb() {
  const { data } = await supabase.from("keywords" as any).select("*").order("term");
  _items = (data ?? []).map(rowToKeyword);
  _loaded = true;
  notify();
}

loadFromDb();

export const keywordStore = {
  get: () => _items,
  subscribe: (fn: Listener) => { listeners.add(fn); return () => listeners.delete(fn); },
  isLoaded: () => _loaded,
  reload: loadFromDb,

  async add(item: Omit<Keyword, "createdAt" | "updatedAt">) {
    const { data, error } = await supabase.from("keywords" as any).insert({
      id: item.id,
      term: item.term,
      definition: item.definition,
      synonyms: item.synonyms,
      glossary_version: item.glossaryVersion ?? "",
      status: item.status,
      certification_ids: item.certificationIds ?? null,
    }).select().single();
    if (!error && data) {
      _items = [..._items, rowToKeyword(data)].sort((a, b) => a.term.localeCompare(b.term));
      notify();
    }
    return { error };
  },

  async update(id: string, patch: Partial<Pick<Keyword, "term" | "definition" | "synonyms" | "glossaryVersion" | "status" | "certificationIds">>) {
    const dbPatch: Record<string, unknown> = {};
    if (patch.term !== undefined) dbPatch.term = patch.term;
    if (patch.definition !== undefined) dbPatch.definition = patch.definition;
    if (patch.synonyms !== undefined) dbPatch.synonyms = patch.synonyms;
    if (patch.glossaryVersion !== undefined) dbPatch.glossary_version = patch.glossaryVersion;
    if (patch.status !== undefined) dbPatch.status = patch.status;
    if (patch.certificationIds !== undefined) dbPatch.certification_ids = patch.certificationIds;

    const { error } = await supabase.from("keywords" as any).update(dbPatch).eq("id", id);
    if (!error) {
      _items = _items.map((k) => (k.id === id ? { ...k, ...patch } : k));
      notify();
    }
    return { error };
  },

  async remove(id: string) {
    const { error } = await supabase.from("keywords" as any).delete().eq("id", id);
    if (!error) {
      _items = _items.filter((k) => k.id !== id);
      notify();
    }
  },
};
